export interface QualityBlock {
  block_type?: string;
  title?: string | null;
  payload_json?: string | null;
}

export interface QualityVocabulary {
  word?: string | null;
}

export interface LessonQuality {
  score: number;
  label: 'Excellent' | 'Strong' | 'Needs work';
  missing: string[];
  counts: {
    blocks: number;
    vocabulary: number;
    exercises: number;
    audio: number;
    visuals: number;
  };
}

function payload(block: QualityBlock): Record<string, unknown> {
  try {
    const value = JSON.parse(block.payload_json || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function scoreLessonContent(blocks: QualityBlock[], vocabulary: QualityVocabulary[]): LessonQuality {
  const parsed = blocks.map((block) => ({ block, payload: payload(block) }));
  const types = new Set(parsed.map(({ block, payload }) => String(payload.type || block.block_type || '').toLowerCase()));
  const exercises = parsed.filter(({ block, payload }) =>
    block.block_type === 'exercise' || payload.type === 'exercise' || typeof payload.exercise === 'object'
  );
  const audio = parsed.filter(({ payload }) =>
    hasText(payload.audioText) || hasText(payload.audio_text) || hasText(payload.audio_url) || hasText(payload.audioUrl)
      || typeof payload.exercise === 'object' && hasText((payload.exercise as Record<string, unknown>).audioText)
  );
  const visuals = parsed.filter(({ block, payload }) =>
    ['image', 'picture_talk', 'visual'].includes(String(payload.type || block.block_type || '').toLowerCase())
      || hasText(payload.image_url) || hasText(payload.imageUrl)
  );
  const metadataReady = parsed.filter(({ payload }) =>
    ['de','en'].includes(String(payload.language)) && /^A0|A1|A2|B1|B2|C1$/.test(String(payload.level))
  );

  const missing: string[] = [];
  const required: Array<[string, boolean]> = [
    ['Vocabulary', types.has('vocabulary')],
    ['Listening', types.has('listening') || audio.length > 0],
    ['Speaking', types.has('speaking')],
    ['Writing', types.has('writing')],
    ['Checkpoint', exercises.some(({ payload }) => String(payload.title || '').toLowerCase().includes('check') || String(payload.exercise && typeof payload.exercise === 'object' ? (payload.exercise as Record<string, unknown>).title : '').toLowerCase().includes('check'))],
  ];
  for (const [name, ok] of required) if (!ok) missing.push(name);

  const vocabularyReady = vocabulary.filter((item) => hasText(item.word)).length >= 4;
  if (!vocabularyReady) missing.push('4+ vocabulary items');
  if (audio.length === 0) missing.push('Natural audio');
  if (visuals.length === 0) missing.push('Visual context');
  if (exercises.length === 0) missing.push('Interactive exercise');
  if (metadataReady.length < Math.max(1, Math.ceil(parsed.length * 0.8))) missing.push('Language + level metadata');

  let score = 0;
  score += required.filter(([, ok]) => ok).length * 8;
  score += Math.min(15, Math.round((metadataReady.length / Math.max(1, parsed.length)) * 15));
  score += audio.length ? 10 : 0;
  score += visuals.length ? 10 : 0;
  score += exercises.length ? 10 : 0;
  score += vocabularyReady ? 10 : Math.min(10, vocabulary.filter((item) => hasText(item.word)).length * 2.5);
  score += parsed.filter(({ block, payload }) => hasText(block.title) && (hasText(payload.text) || hasText(payload.prompt) || typeof payload.exercise === 'object')).length >= Math.max(1, Math.ceil(parsed.length * 0.8)) ? 5 : 0;
  score = Math.max(0, Math.min(100, Math.round(score)));

  return {
    score,
    label: score >= 90 ? 'Excellent' : score >= 75 ? 'Strong' : 'Needs work',
    missing,
    counts: { blocks: blocks.length, vocabulary: vocabulary.filter((item) => hasText(item.word)).length, exercises: exercises.length, audio: audio.length, visuals: visuals.length },
  };
}
